/**
 * 2020-2024 Stadt Wolfsburg
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import {connect} from 'react-redux';

import PropTypes from 'prop-types';
import {LayerRole, addLayerFeatures, removeLayerFeatures, clearLayer, changeLayerProperty, addMarker, removeMarker} from 'qwc2/actions/layers';
import {changeZoomLevel, panTo, zoomToExtent, zoomToPoint, changeRotation, toggleMapTips} from 'qwc2/actions/map';
import {setCurrentTask} from 'qwc2/actions/task';
import {showIframeDialog, showNotification, closeWindow, closeAllWindows} from 'qwc2/actions/windows';
import LayerUtils from 'qwc2/utils/LayerUtils';
import MapUtils from 'qwc2/utils/MapUtils';
import VectorLayerUtils from 'qwc2/utils/VectorLayerUtils';

class WobPostMessageAPI extends React.Component {
    static propTypes = {
        allowedOrigins: PropTypes.array,

        // QWC2 objects
        layers: PropTypes.array,
        map: PropTypes.object,

        // QWC2 map functions
        changeRotation: PropTypes.func,
        changeZoomLevel: PropTypes.func,
        panTo: PropTypes.func,
        toggleMapTips: PropTypes.func,
        zoomToExtent: PropTypes.func,
        zoomToPoint: PropTypes.func,

        // QWC2 layers functions
        addLayerFeatures: PropTypes.func,
        addMarker: PropTypes.func,
        changeLayerProperty: PropTypes.func,
        clearLayer: PropTypes.func,
        removeLayerFeatures: PropTypes.func,
        removeMarker: PropTypes.func,

        // QWC2 windows functions
        closeAllWindows: PropTypes.func,
        closeWindow: PropTypes.func,
        showIframeDialog: PropTypes.func,
        showNotification: PropTypes.func,

        // QWC2 task functions
        setCurrentTask: PropTypes.func
    };
    static defaultProps = {
        allowedOrigins: []
    };
    componentDidMount() {
        window.addEventListener('message', this.receiveMessage);
    }
    componentWillUnmount() {
        window.removeEventListener('message', this.receiveMessage);
    }
    render() {
        return null;
    }
    receiveMessage = (event) => {
        const result = {
            type: "QWC2ApiResult",
            name: "",
            uuid: "",
            result: false
        };

        // Check allowed origins
        if (this.verifyMessageIdentity(event) !== true) {
            return result;
        }

        // Check type
        if (this.getMessageStringParam("type", event.data) !== "QWC2ApiRequest") {
            return result;
        }

        // Determine function name
        const functionName = this.getMessageStringParam("name", event.data);
        result.name = functionName;

        // Determine function UUID
        const functionUuid = this.getMessageStringParam("uuid", event.data);
        result.uuid = functionUuid;

        // Determine function params
        const params = this.getMessageParam("params", event.data);

        // Find and execute matching function
        switch (functionName) {
        // Map functions
        case "zoom":
            result.result = this.zoom(this.getMessageNumberParam("direction", params));
            break;
        case "zoomToExtent":
            result.result = this.zoomToExtent(this.getMessageNumberArrayParam("extent", params), this.getMessageStringParam("crs", params));
            break;
        case "zoomToPoint":
            result.result = this.zoomToPoint(this.getMessageNumberArrayParam("pos", params), this.getMessageNumberParam("scale", params), this.getMessageStringParam("crs", params));
            break;
        case "panTo":
            result.result = this.panTo(this.getMessageNumberArrayParam("pos", params), this.getMessageStringParam("crs", params));
            break;
        case "changeRotation":
            result.result = this.changeRotation(this.getMessageNumberParam("rotation", params));
            break;
        case "toggleMapTips":
            result.result = this.toggleMapTips(this.getMessageBooleanParam("active", params));
            break;

        // Layers functions
        case "zoomToLayer":
            result.result = this.zoomToLayer(this.getMessageStringParam("layerName", params));
            break;
        case "setLayerVisibility":
            result.result = this.setLayerVisibility(this.getMessageStringParam("layerName", params), this.getMessageBooleanParam("visibile", params));
            break;
        case "setLayersVisibility":
            result.result = this.setLayersVisibility(this.getMessageStringArrayParam("layerNames", params), this.getMessageBooleanParam("visibile", params));
            break;
        case "toggleLayerVisibility":
            result.result = this.toggleLayerVisibility(this.getMessageStringParam("layerName", params));
            break;
        case "toggleLayersVisibility":
            result.result = this.toggleLayersVisibility(this.getMessageStringArrayParam("layerNames", params));
            break;
        case "setBackgroundLayer":
            result.result = this.setBackgroundLayer(this.getMessageStringParam("backgroundLayerName", params));
            break;
        case "addLayerFeatures":
            result.result = this.addLayerFeatures(this.getMessageParam("layer", params), this.getMessageParam("features", params), this.getMessageBooleanParam("clear", params));
            break;
        case "removeLayerFeatures":
            result.result = this.removeLayerFeatures(this.getMessageStringParam("layerId", params), this.getMessageStringArrayParam("featureIds", params), this.getMessageBooleanParam("keepEmptyLayer", params));
            break;
        case "clearLayer":
            result.result = this.clearLayer(this.getMessageStringParam("layerId", params));
            break;
        case "addMarker":
            result.result = this.addMarker(this.getMessageStringParam("id", params), this.getMessageNumberArrayParam("pos", params), this.getMessageStringParam("label", params), this.getMessageStringParam("crs", params), this.getMessageNumberParam("zIndex", params));
            break;
        case "removeMarker":
            result.result = this.removeMarker(this.getMessageStringParam("id", params));
            break;

        // Windows functions
        case "showIframeDialog":
            result.result = this.showIframeDialog(this.getMessageStringParam("name", params), this.getMessageStringParam("url", params), this.getMessageParam("options", params));
            break;
        case "showNotification":
            result.result = this.showNotification(this.getMessageStringParam("name", params), this.getMessageStringParam("text", params));
            break;
        case "closeWindow":
            result.result = this.closeWindow(this.getMessageStringParam("name", params));
            break;
        case "closeAllWindows":
            result.result = this.closeAllWindows();
            break;

        // Other functions
        case "drawScratch":
            /* eslint-disable-next-line */
            console.warn("drawScratch is deprecated, use drawGeometry instead");
            result.result = this.drawScratch(event, result.uuid, this.getMessageStringParam("geomType", params), this.getMessageStringParam("message", params), this.getMessageBooleanParam("drawMultiple", params), this.getMessageParam("style", params), this.getMessageBooleanParam("returnGeoJson", params));
            break;
        case "drawGeometry":
            result.result = this.drawGeometry(event, result.uuid, this.getMessageStringParam("geomType", params), this.getMessageStringParam("message", params), this.getMessageParam("options", params), this.getMessageBooleanParam("returnGeoJson", params));
            break;
        default:
            break;
        }

        // Send back result message
        event.source.postMessage(result, event.origin);

        return result;
    };
    verifyMessageIdentity = (event) => {
        let result = false;
        try {
            for (let i = 0; i < this.props.allowedOrigins.length; i++) {
                if (event.origin === this.props.allowedOrigins[i]) {
                    result = true;
                    break;
                }
            }
        } catch (e) {
            // Pass
        }
        return result;
    };
    getMessageParam = (key, params) => {
        let result;
        try {
            if (key && params) {
                if (key in params) {
                    if (Object.prototype.hasOwnProperty.call(params, key)) {
                        result = params[key];
                    }
                }
            }
        } catch (e) {
            // Pass
        }
        return result;
    };
    getMessageStringParam = (key, params) => {
        let result = this.getMessageParam(key, params);
        if (result !== undefined) {
            try {
                result = String(result);
            } catch (e) {
                result = undefined;
            }
        }
        return result;
    };
    getMessageStringArrayParam = (key, params) => {
        let result = this.getMessageParam(key, params);
        if (result !== undefined) {
            if (Array.isArray(result)) {
                try {
                    const newResult = [];
                    result.forEach(value => {
                        newResult.push(String(value));
                    });
                    result = newResult;
                } catch (e) {
                    result = undefined;
                }
            } else {
                result = undefined;
            }
        }
        return result;
    };
    getMessageNumberParam = (key, params) => {
        let result = this.getMessageParam(key, params);
        if (result !== undefined) {
            try {
                result = Number(result);
            } catch (e) {
                result = undefined;
            }
        }
        return result;
    };
    getMessageNumberArrayParam = (key, params) => {
        let result = this.getMessageParam(key, params);
        if (result !== undefined) {
            if (Array.isArray(result)) {
                try {
                    const newResult = [];
                    result.forEach(value => {
                        newResult.push(Number(value));
                    });
                    result = newResult;
                } catch (e) {
                    result = undefined;
                }
            } else {
                result = undefined;
            }
        }
        return result;
    };
    getMessageBooleanParam = (key, params) => {
        let result = this.getMessageParam(key, params);
        if (result !== undefined) {
            try {
                switch (String(result)) {
                case "true":
                    result = true;
                    break;
                case "false":
                    result = false;
                    break;
                default:
                    break;
                }
            } catch (e) {
                result = undefined;
            }
        }
        return result;
    };
    verifyValidValue = (value, type) => {
        if (value && typeof value === type) {
            return true;
        } else {
            return false;
        }
    };

    // Map functions
    zoom = (direction) => {
        let result = false;
        if (direction !== undefined) {
            let disabled = false;
            if (direction > 0) {
                disabled = this.props.map.zoom >= this.props.map.resolutions.length - 1;
            } else if (direction < 0) {
                disabled = this.props.map.zoom <= 0;
            }
            if (!disabled) {
                this.props.changeZoomLevel(this.props.map.zoom + direction);
                result = true;
            }
        }
        return result;
    };
    zoomToExtent = (extent, crs) => {
        let result = false;
        if (extent !== undefined && crs !== undefined) {
            this.props.zoomToExtent(extent, crs);
            result = true;
        }
        return result;
    };
    zoomToPoint = (pos, scale, crs) => {
        let result = false;
        if (pos !== undefined && scale !== undefined && crs !== undefined) {
            const zoom = MapUtils.computeZoom(this.props.map.scales, scale);
            this.props.zoomToPoint(pos, zoom, crs);
            result = true;
        }
        return result;
    };
    panTo = (pos, crs) => {
        let result = false;
        if (pos !== undefined && crs !== undefined) {
            this.props.panTo(pos, crs);
            result = true;
        }
        return result;
    };
    changeRotation = (rotation) => {
        let result = false;
        if (rotation !== undefined) {
            let angle = parseFloat(rotation) || 0;
            while (angle < 0) {
                angle += 360;
            }
            while (angle >= 360) {
                angle -= 360;
            }
            this.props.changeRotation(angle / 180.0 * Math.PI);
            result = true;
        }
        return result;
    };
    toggleMapTips = (active) => {
        let result = false;
        if (active !== undefined) {
            this.props.toggleMapTips(active);
            result = true;
        }
        return result;
    };

    // Layers functions
    zoomToLayer = (layerName) => {
        let result = false;
        if (layerName !== undefined) {
            for (const layer of this.props.layers) {
                if (layer.role === LayerRole.THEME) {
                    let resultLayer = null;

                    // If equal name
                    if (layer.name === layerName) {
                        resultLayer = layer;
                    // If layer has sublayers
                    } else if (layer.sublayers) {
                        const layerpath = [];
                        const searchResult = LayerUtils.searchSubLayer(layer, "name", layerName, layerpath);

                        if (searchResult) {
                            const clonedLayer = LayerUtils.cloneLayer(layer, layerpath);
                            resultLayer = clonedLayer.newsublayer;
                        }
                    }

                    // If matching layer
                    if (resultLayer) {
                        if (resultLayer.bbox && resultLayer.bbox.bounds && resultLayer.bbox.crs) {
                            this.props.zoomToExtent(resultLayer.bbox.bounds, resultLayer.bbox.crs);
                            result = true;
                        }
                        // End loop
                        break;
                    }
                }
            }
        }
        return result;
    };
    setLayerVisibility = (layerName, visibile = null) => {
        let result = false;
        if (layerName !== undefined) {
            for (const layer of this.props.layers) {
                if (layer.role === LayerRole.THEME) {
                    let uuid = null;
                    let oldvisibility = layer.visibility;
                    let grouppath = [];
                    let recurseDirection = null;

                    // If equal name
                    if (layer.name === layerName) {
                        uuid = layer.uuid;
                    // If layer has sublayers
                    } else if (layer.sublayers) {
                        const layerpath = [];
                        const searchResult = LayerUtils.searchSubLayer(layer, "name", layerName, layerpath);

                        if (searchResult) {
                            const clonedLayer = LayerUtils.cloneLayer(layer, layerpath);
                            uuid = layer.uuid;
                            oldvisibility = clonedLayer.newsublayer.visibility;
                            grouppath = layerpath;
                        }
                    }

                    // If matching layer
                    if (uuid) {
                        // New visibility
                        let newvisibility;

                        if (visibile === true) {
                            newvisibility = true;
                        } else if (visibile === false) {
                            newvisibility = false;
                        } else {
                            newvisibility = !oldvisibility;
                        }

                        // If layer becomes visible, also make parents visible
                        recurseDirection = newvisibility ? "both" : "children";

                        this.props.changeLayerProperty(uuid, "visibility", newvisibility, grouppath, recurseDirection);

                        result = true;

                        // End loop
                        break;
                    }
                }
            }
        }
        return result;
    };
    setLayersVisibility = (layerNames, visibile = null) => {
        let result = false;
        let currentResult = false;
        if (layerNames !== undefined) {
            for (let i = 0; i < layerNames.length; i++) {
                currentResult = this.setLayerVisibility(layerNames[i], visibile);
                result = result && currentResult ? true : false;
            }
        }
        return result;
    };
    toggleLayerVisibility = (layerName) => {
        return this.setLayerVisibility(layerName);
    };
    toggleLayersVisibility = (layerNames) => {
        let result = false;
        let currentResult = false;
        if (layerNames !== undefined) {
            for (let i = 0; i < layerNames.length; i++) {
                currentResult = this.toggleLayerVisibility(layerNames[i]);
                result = result && currentResult ? true : false;
            }
        }
        return result;
    };
    setBackgroundLayer = (backgroundLayerName) => {
        let result = false;
        if (backgroundLayerName !== undefined) {
            for (const layer of this.props.layers) {
                if (layer.role === LayerRole.BACKGROUND) {
                    // If equal name
                    if (layer.name === backgroundLayerName) {
                        // Change layer visibility
                        this.props.changeLayerProperty(layer.uuid, "visibility", true);
                        result = true;

                        // End loop
                        break;
                    }
                }
            }
        }
        return result;
    };
    addLayerFeatures = (layer, features, clear) => {
        let result = false;
        if (layer !== undefined && features !== undefined && clear !== undefined) {
            this.props.addLayerFeatures(layer, features, clear);
            result = true;
        }
        return result;
    };
    removeLayerFeatures = (layerId, featureIds, keepEmptyLayer) => {
        let result = false;
        if (layerId !== undefined && featureIds !== undefined && keepEmptyLayer !== undefined) {
            this.props.removeLayerFeatures(layerId, featureIds, keepEmptyLayer);
            result = true;
        }
        return result;
    };
    clearLayer = (layerId) => {
        let result = false;
        if (layerId !== undefined) {
            this.props.clearLayer(layerId);
            result = true;
        }
        return result;
    };
    addMarker = (id, pos, label, crs, zIndex) => {
        let result = false;
        if (id !== undefined && pos !== undefined && label !== undefined && crs !== undefined && zIndex !== undefined) {
            this.props.addMarker(id, pos, label, crs, zIndex);
            result = true;
        }
        return result;
    };
    removeMarker = (id) => {
        let result = false;
        if (id !== undefined) {
            this.props.removeMarker(id);
            result = true;
        }
        return result;
    };

    // Windows functions
    showIframeDialog = (name, url, options) => {
        let result = false;
        if (name !== undefined && url !== undefined && options !== undefined) {
            this.props.showIframeDialog(name, url, options);
            result = true;
        }
        return result;
    };
    showNotification = (name, text) => {
        let result = false;
        if (name !== undefined && text !== undefined) {
            this.props.showNotification(name, text);
            result = true;
        }
        return result;
    };
    closeWindow = (name) => {
        let result = false;
        if (name !== undefined) {
            this.props.closeWindow(name);
            result = true;
        }
        return result;
    };
    closeAllWindows = () => {
        const result = true;
        this.props.closeAllWindows();
        return result;
    };

    // Other functions
    drawScratch = (messageEvent, uuid, geomType, message, drawMultiple, style, returnGeoJson) => {
        let result = false;
        if (messageEvent && geomType !== undefined && message !== undefined && drawMultiple !== undefined && style !== undefined && returnGeoJson !== undefined) {
            const data = {
                geomType: geomType,
                message: message,
                drawMultiple: drawMultiple,
                callback: (features, crs) => {
                    let messageResult;
                    if (returnGeoJson) {
                        messageResult = JSON.stringify({
                            type: "FeatureCollection",
                            features: features.map(feature => ({
                                ...feature,
                                geometry: VectorLayerUtils.reprojectGeometry(feature.geometry, crs || this.props.map.projection, 'EPSG:4326')
                            }))
                        });
                    } else {
                        messageResult = {
                            features: features,
                            crs: crs
                        };
                    }
                    messageEvent.source.postMessage(
                        {
                            type: "QWC2ApiResult",
                            name: "drawScratchResult",
                            uuid: uuid,
                            result: messageResult
                        },
                        messageEvent.origin
                    );
                },
                style: style
            };
            this.props.setCurrentTask("ScratchDrawing", null, null, data);
            result = true;
        }
        return result;
    };
    drawGeometry = (messageEvent, uuid, geomType, message, options, returnGeoJson) => {
        let result = false;
        if (messageEvent && geomType !== undefined && message !== undefined && options !== undefined && returnGeoJson !== undefined) {
            const data = {
                geomType: geomType,
                message: message,
                drawMultiple: options.drawMultiple || false,
                style: options.style,
                snapping: options.snapping || false,
                snappingActive: options.snappingActive || false,
                initialFeatures: options.initialFeatures,
                callback: (features, crs) => {
                    let messageResult;
                    if (returnGeoJson) {
                        messageResult = JSON.stringify({
                            type: "FeatureCollection",
                            features: features.map(feature => ({
                                ...feature,
                                geometry: VectorLayerUtils.reprojectGeometry(feature.geometry, crs || this.props.map.projection, 'EPSG:4326')
                            }))
                        });
                    } else {
                        messageResult = {
                            features: features,
                            crs: crs
                        };
                    }
                    messageEvent.source.postMessage(
                        {
                            type: "QWC2ApiResult",
                            name: "drawGeometryResult",
                            uuid: uuid,
                            result: messageResult
                        },
                        messageEvent.origin
                    );
                }
            };
            this.props.setCurrentTask("drawGeometry", null, null, data);
            result = true;
        }
        return result;
    };
}

export default connect(state => ({
    map: state.map,
    layers: state.layers && state.layers.flat ? state.layers.flat : []
}), {
    changeZoomLevel: changeZoomLevel,
    panTo: panTo,
    zoomToExtent: zoomToExtent,
    zoomToPoint: zoomToPoint,
    changeRotation: changeRotation,
    toggleMapTips: toggleMapTips,
    showIframeDialog: showIframeDialog,
    showNotification: showNotification,
    closeWindow: closeWindow,
    closeAllWindows: closeAllWindows,
    addLayerFeatures: addLayerFeatures,
    removeLayerFeatures: removeLayerFeatures,
    clearLayer: clearLayer,
    changeLayerProperty: changeLayerProperty,
    addMarker: addMarker,
    removeMarker: removeMarker,
    setCurrentTask: setCurrentTask
})(WobPostMessageAPI);
